import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './config/axios';  // Register global 401 interceptor
import { ShoppingCart, ArrowLeft } from 'lucide-react';
import TenantSwitcher from './components/tenant/TenantSwitcher';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { toast, Toaster } from './components/ui/sonner';
import { setAuth, getToken, getUser, clearAuth, getAuthHeaders, forceCleanAuth, navigateToDashboard, getDashboardPath, formatTuition, getUniversityLogoUrl, getUniversityFallbackUrl, API_URL } from './utils/auth';
import RoleGuard, { AdminGuard, SuperAdminGuard, GovernanceGuard, PartnerGuard } from './components/auth/RoleGuard';
// TmpLabsLogo import removed - using GlobeMark + EazySparkIcon instead
import PoweredByFooter from './components/brand/PoweredByFooter';
import RoleAccessDemo from './components/debug/RoleAccessDemo';
import WhatsAppChatButton from './components/WhatsAppChatButton';
import GuruMascot from './components/GuruMascot';
import GlobeMark from './components/shared/GlobeMark';
import PhoneCaptureModal from './components/PhoneCaptureModal';
import EazySparkIcon from './components/shared/EazySparkIcon';
import PlatformLogo from './components/shared/PlatformLogo';
import ECEWordmark from './components/shared/ECEWordmark';
import { ThemeProvider } from './context/ThemeContext';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import ThemeToggle from './components/theme/ThemeToggle';
import BottomNavigation from './components/mobile/BottomNavigation';
import MobileHeader from './components/mobile/MobileHeader';
import { useMobile } from './hooks/useMobile';
import NrouteLanding from './pages/NrouteLanding';
import ResetPasswordPage from './pages/ResetPasswordPage';
import './App.css';

// Force mobile-accessible API URL
const BACKEND_URL = window.location.hostname === 'localhost' ?
  'http://localhost:8001' :
  ''; // Use relative URL for production (goes through ingress)
const API = window.location.hostname === 'localhost' ?
  'http://localhost:8001/api' :
  '/api'; // Use relative path for HTTPS

// Debug logging
console.log('🔧 Current hostname:', window.location.hostname);
console.log('🔧 Backend URL:', BACKEND_URL);
console.log('🔧 API URL:', API);

// Configure axios to include cookies in all requests
axios.defaults.withCredentials = true;

// Expose auth cleaning function for debugging
window.forceCleanAuth = forceCleanAuth;

// NrouteLanding (premium marketing landing at "/") imported above from ./pages/NrouteLanding.jsx

// Auth Pages
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBranding(); // v2.37: Tenant-aware branding
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Preserve partner params (e.g., ?partner=eazylink&utm_source=brochure) for registration
  const registerUrl = `/register${location.search}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      console.log('Login successful, user role:', response.data.user.role);
      console.log('Token received:', response.data.token ? 'Yes' : 'No');

      // Store auth data in localStorage for API requests
      // Also store token for Authorization header in API calls
      setAuth(response.data.token, response.data.user);

      toast.success('Welcome back!');
      
      // Route based on user role
      const userRole = response.data.user.role;
      console.log('User role for navigation:', userRole);
      
      // Determine target path
      // Phase 2: honor ?redirect= query param from 401 interceptor
      // Open-redirect mitigation: only allow relative paths starting
      // with single /, reject protocol-relative // and external URLs
      const searchParams = new URLSearchParams(location.search);
      const rawRedirect = searchParams.get('redirect');
      const redirectPath = (rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//'))
        ? rawRedirect
        : null;

      let targetPath;
      if (redirectPath) {
        targetPath = redirectPath;
      } else if (userRole === 'superadmin') {
        targetPath = '/superadmin-dashboard';
      } else if (userRole === 'partner' || userRole === 'tenant_admin') {
        targetPath = '/partner-dashboard';
      } else {
        targetPath = '/dashboard';
      }
      
      console.log('Target path:', targetPath);
      
      // Small delay to ensure cookies are set before navigation
      setTimeout(() => {
        navigate(targetPath, { replace: true });
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Call our backend to get Google OAuth URL
      const response = await axios.get(`${API}/auth/google/login`);
      // Redirect to Google
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google login unavailable. Please try again or use email login.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSuccess(false);
  };

  return (
    <div className="flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4" style={{ minHeight: "100dvh" }}>
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeForgotPassword}>
          <Card className="w-full max-w-md bg-slate-800 border-slate-700/50" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="space-y-4 text-center pt-8">
              {/* Email Icon */}
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <CardTitle className="text-2xl text-white">Forgot Password?</CardTitle>
              <CardDescription className="text-slate-300">
                {forgotSuccess
                  ? "Check your email for instructions"
                  : "Enter your email address and we'll send you instructions to reset your password."
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-8">
              {forgotSuccess ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm text-slate-300 mb-6">
                    Check your inbox! Reset link on the way.
                  </p>
                  <Button onClick={closeForgotPassword} className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                    Back to Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="Enter your email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white h-12" disabled={forgotLoading}>
                    {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <div className="text-center">
                    <span className="text-slate-400 text-sm">Remember it? </span>
                    <Button type="button" variant="link" className="p-0 h-auto text-sm text-blue-400 hover:text-blue-300" onClick={closeForgotPassword}>
                      Back to Login
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="w-full max-w-md bg-slate-800/80 backdrop-blur-sm border-slate-700/50">
        <CardHeader className="space-y-1 text-center">
          <div className="flex flex-col items-center justify-center mb-4 gap-2">
            {/* Logo - responsive sizing */}
            <PlatformLogo size={window.innerWidth < 640 ? 40 : 56} showName={false} />
            <div className="flex flex-col items-center">
              {/* Wordmark + tagline - responsive sizing: sm on mobile, lg on desktop */}
              {/* Force light text for dark login card background */}
              <ECEWordmark size={window.innerWidth < 640 ? "sm" : "lg"} showTagline={true} forceLightText={true} />
            </div>
          </div>
          <CardTitle className="text-2xl text-white">Welcome back</CardTitle>
          <CardDescription className="text-slate-300">Let's get you back in</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Social Login First */}
          <Button variant="outline" className="w-full h-11 bg-slate-700/50 border-slate-600 text-white hover:bg-slate-700" onClick={handleGoogleLogin} data-testid="google-login-btn">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>

          {/* Or Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-slate-800 text-slate-400">or</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Input
                id="email"
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Input
                id="password"
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                autoComplete="current-password"
                className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="login-password-input"
              />
            </div>
            <div className="text-left">
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto text-sm text-blue-400 hover:text-blue-300"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot password?
              </Button>
            </div>
            <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-slate-400">New here? </span>
            <Button variant="link" className="p-0 h-auto text-blue-400 hover:text-blue-300 font-semibold" onClick={() => navigate(registerUrl)}>Create account</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBranding(); // v2.37.1: Tenant-aware branding (HOTFIX)
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', name: '', role: 'student' });
  const [loading, setLoading] = useState(false);

  const handleGoogleSignup = async () => {
    try {
      // Call our backend to get Google OAuth URL
      const response = await axios.get(`${API}/auth/google/login`);
      // Redirect to Google
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Google signup error:', error);
      toast.error('Google signup unavailable. Please use the form below.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // Check if this is a partner referral signup (supports both 'partner' and legacy 'ref' params)
      const searchParams = new URLSearchParams(location.search);
      const partnerCode = searchParams.get('partner') || searchParams.get('ref');
      const hasPartnerReferral = partnerCode ||
                                searchParams.get('utm_source') ||
                                document.cookie.includes('partner_referral');

      // Log partner referral context
      console.log('📋 Registration context:', { hasPartnerReferral, partner: partnerCode });
      
      // Use partner-aware student signup endpoint for students with referrals
      let endpoint = `${API}/auth/register`;
      
      if (formData.role === 'student' && hasPartnerReferral) {
        endpoint = `${API}/auth/student-signup`;
        console.log('🎯 Using partner-aware student signup endpoint');
        console.log('📋 Partner referral detected:', {
          partner: partnerCode,
          utm_source: searchParams.get('utm_source'),
          utm_medium: searchParams.get('utm_medium')
        });
      } else {
        console.log('🎯 Using regular registration endpoint');
      }

      // Prepare request data with partner context if detected
      let requestData = formData;
      let headers = {};

      if (formData.role === 'student' && hasPartnerReferral) {
        // Add partner context headers for the backend
        headers = {
          'X-Partner-Code': partnerCode || 'eazylink',
          'X-Referral-Source': searchParams.get('utm_source') || 'qr_code',
          'X-UTM-Medium': searchParams.get('utm_medium') || 'qr',
          'X-Campaign': searchParams.get('utm_campaign') || 'qr_marketing'
        };

        console.log('📡 Sending partner context headers:', headers);
      }
      
      const response = await axios.post(endpoint, requestData, { headers });
      
      setAuth(response.data.token, response.data.user);
      toast.success('Account created successfully!');
      
      if (response.data.user.role === 'partner') {
        navigate('/partner-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4" style={{ minHeight: "100dvh" }}>
      <Card className="w-full max-w-md bg-slate-800/80 backdrop-blur-sm border-slate-700/50">
        <CardHeader className="space-y-1 text-center">
          <div className="flex flex-col items-center justify-center mb-4 gap-2">
            <PlatformLogo size={56} showName={false} />
            <div className="flex flex-col items-center">
              <ECEWordmark size="lg" showTagline={true} />
            </div>
          </div>
          <CardTitle className="text-2xl text-white">Let's do this</CardTitle>
          <CardDescription className="text-slate-300">Your study abroad journey starts here</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Social Login First */}
          <Button
            variant="outline"
            className="w-full h-11 bg-slate-700/50 border-slate-600 text-white hover:bg-slate-700"
            onClick={handleGoogleSignup}
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>

          {/* Or Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-slate-800 text-slate-400">or</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              id="name"
              placeholder="Your name"
              className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              id="email"
              type="email"
              placeholder="Email"
              className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              id="password"
              type="password"
              placeholder="Password"
              className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              autoComplete="new-password"
            />
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm password"
              className="h-11 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              autoComplete="new-password"
            />
            <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white" disabled={loading}>
              {loading ? 'Creating...' : 'Get Started'}
            </Button>
          </form>

          {/* Terms and Privacy */}
          <p className="text-xs text-center text-slate-400">
            By signing up, you agree to our{' '}
            <a href="/terms" className="text-blue-400 hover:text-blue-300">Terms</a>
            {' '}and{' '}
            <a href="/privacy" className="text-blue-400 hover:text-blue-300">Privacy Policy</a>
          </p>

          {/* Already have account */}
          <div className="text-center text-sm">
            <span className="text-slate-400">Already have an account? </span>
            <Button variant="link" className="p-0 h-auto text-blue-400 hover:text-blue-300" onClick={() => navigate('/login')}>Sign in</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Terms of Service Page
const TermsOfServicePage = () => {
  const navigate = useNavigate();
  const branding = useBranding(); // v2.37: Tenant-aware branding
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  const sections = [
    { id: 'about', title: '1. About /tmp labs' },
    { id: 'accounts', title: '2. User Accounts and Security' },
    { id: 'informational', title: '3. Informational Purposes Only' },
    { id: 'privacy', title: '4. Privacy and Cookie Policy' },
    { id: 'international', title: '5. International Use and Access' },
    { id: 'ip', title: '6. Intellectual Property' },
    { id: 'thirdparty', title: '7. Linked Content and Third Party Sites' },
    { id: 'prohibited', title: '8. Prohibited Uses' },
    { id: 'posting', title: '9. Posting Guidelines' },
    { id: 'social', title: '10. Social Media' },
    { id: 'children', title: '11. Children\'s Privacy' },
    { id: 'partner', title: '12. Partner Agencies' },
    { id: 'modifications', title: '13. Modifications and Termination' },
    { id: 'liability', title: '14. Limitation of Liability' },
    { id: 'indemnification', title: '15. Indemnification' },
    { id: 'governing', title: '16. Governing Law and Jurisdiction' },
    { id: 'contact', title: '17. Notice and Contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-edu-blue/5" style={{ minHeight: "100dvh" }}>
      {/* Brand Header */}
      <div className="bg-gradient-to-r from-edu-blue to-edu-blue-dark text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="mr-4 text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Terms of Use</h1>
                <p className="text-white/80 text-sm">Legal agreement for using our services</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-2 text-sm text-white/80">
              <span>Last Updated: January 2025</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Sidebar */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="lg:sticky lg:top-4">
              <Card className="shadow-lg border-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Table of Contents
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <nav className="space-y-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg transition-colors hover:bg-edu-blue/10 hover:text-edu-blue text-gray-600"
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Card className="shadow-lg border-0">
              <CardContent className="p-8 lg:p-10">
                {/* Mobile Last Updated */}
                <p className="text-sm text-gray-500 mb-6 md:hidden">Last Updated: January 2025</p>

                {/* Section 1 */}
                <div id="about" className="scroll-mt-4">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3">1</span>
                    About /tmp labs
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              These Terms of Use ("Terms") apply to all websites, applications (including mobile apps), and platforms
              owned or operated by /tmp labs pte. ltd and its affiliates ("/tmp labs"). This includes, without
              limitation, {branding?.platform_name || '∑n-route'} and related subdomains or successor sites (collectively, the "Websites").
            </p>
            <p className="text-gray-600 mb-4">
              By accessing or using any part of the Websites, you agree to be legally bound by these Terms. If you
              do not agree, you must not use the Websites.
            </p>
            <p className="text-gray-600 mb-4">
              /tmp labs reserves the right to update these Terms at any time. Your continued use of the Websites after
              any such update constitutes your acceptance of the revised Terms. You should review these Terms periodically.
            </p>

                <div id="accounts" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">2</span>
                    User Accounts and Security
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">If you create an account on any of the Websites, you must:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Use only one account per individual;</li>
              <li>Keep your credentials secure and confidential;</li>
              <li>Not share your login information with anyone else;</li>
              <li>Promptly notify /tmp labs of any unauthorized access or security breach.</li>
            </ul>
            <p className="text-gray-600 mb-4">
              You are solely responsible for maintaining the confidentiality of your account and any activities that
              occur under it. You must not use another person's account or share your credentials. /tmp labs disclaims
              all liability arising from any unauthorized use of your account. You may be held liable for losses
              suffered by /tmp labs or others due to such misuse.
            </p>

                <div id="informational" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">3</span>
                    Informational Purposes Only
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              All content provided on the Websites is for general informational purposes only. While we aim to keep
              the information current and accurate, /tmp labs makes no representations or warranties of any kind,
              express or implied, about the completeness, accuracy, reliability, or suitability of any content.
              /tmp labs does not guarantee admission to any educational institution or specific outcomes from the
              use of its services.
            </p>
            <p className="text-gray-600 mb-4">
              /tmp labs is not affiliated with or endorsed by any university, college, or educational institution
              mentioned on the Websites. Any references to such institutions are for informational purposes only.
            </p>

                <div id="privacy" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">4</span>
                    Privacy and Cookie Policy
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              Your use of the Websites is subject to /tmp labs's <a href="/privacy" className="text-edu-blue hover:underline">Privacy Policy</a> which
              outlines how we collect, use, and protect your data.
            </p>

                <div id="international" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">5</span>
                    International Use and Access
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              /tmp labs makes no representation that the Websites, or the content and materials available through them,
              are appropriate or available for use in all locations. Accessing the Websites from jurisdictions where
              such content is illegal or restricted is prohibited. If you choose to access the Websites from outside
              your country of residence, you do so on your own initiative and are responsible for compliance with all
              applicable local laws, regulations, and restrictions.
            </p>

                <div id="ip" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">6</span>
                    Intellectual Property
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              All content on the Websites — including but not limited to text, graphics, videos, images, logos,
              software, and course materials — is the intellectual property of /tmp labs or its licensors and is
              protected by applicable copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-600 mb-4">
              /tmp labs grants you a limited, non-exclusive, revocable license to access and use the Websites for
              your personal, non-commercial use only. Except as expressly permitted, you may not copy, reproduce,
              distribute, modify, publicly display, or otherwise use any content without /tmp labs's prior written permission.
            </p>
            <p className="text-gray-600 mb-4">
              You may only download and use content that is explicitly marked as downloadable (e.g., brochures or
              datasheets), and solely for your personal, non-commercial use. Such content must not be modified,
              redistributed, or presented in a misleading context.
            </p>
            <p className="text-gray-600 mb-4">
              "{branding?.platform_name || '∑n-route'}," "/tmp labs," and the {branding?.platform_name || '∑n-route'} logo are trademarks of /tmp labs pte. ltd. They may
              not be used without prior written permission.
            </p>

                <div id="thirdparty" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">7</span>
                    Linked Content and Third Party Sites
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              The Websites may contain links to external sites or third-party resources, including forums,
              communications tools, or integrated services. These are provided for convenience only. /tmp labs
              does not control and is not responsible for the content, availability, or practices of any
              third-party websites or services.
            </p>
            <p className="text-gray-600 mb-4">
              You acknowledge and agree that /tmp labs is not liable for any loss or damage arising from your use
              of or reliance on any content, products, or services available through third-party links. Links to
              external sites do not imply endorsement of those sites, their content, or their operators.
            </p>
            <p className="text-gray-600 mb-4">
              You are solely responsible for evaluating and taking precautions to protect your system from viruses,
              malware, or other harmful content. /tmp labs does not warrant that the Websites will be error-free,
              uninterrupted, or secure.
            </p>

                <div id="prohibited" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">8</span>
                    Prohibited Uses
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              You agree not to use the Websites for any unlawful or prohibited purpose. This includes, but is not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Engaging in illegal, fraudulent, or deceptive activities;</li>
              <li>Infringing upon the intellectual property rights of /tmp labs or any third party;</li>
              <li>Transmitting, uploading, or distributing harmful, offensive, or otherwise objectionable content;</li>
              <li>Distributing malware, spyware, or spam;</li>
              <li>Scraping, harvesting, or data mining without prior written consent;</li>
              <li>Compromising or attempting to compromise the security or integrity of any system, network, or account.</li>
            </ul>
            <p className="text-gray-600 mb-4">In addition, you must not:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Use any robot, spider, scraper, or other automated means to access or interact with the Websites without our express written permission;</li>
              <li>Probe, scan, or test the vulnerability of the Websites or any connected systems, or breach authentication or security measures;</li>
              <li>Forge headers or otherwise manipulate identifiers to disguise the origin of any communication, or impersonate any person or entity;</li>
              <li>Take any action that imposes an unreasonable or disproportionately large load on /tmp labs's infrastructure, or interferes with any other user's access to or use of the Websites.</li>
            </ul>

                <div id="posting" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">9</span>
                    Posting Guidelines
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">Where applicable, users may post content on the Websites, subject to the following rules:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Do not post content that is unlawful, defamatory, obscene, abusive, or discriminatory;</li>
              <li>Do not impersonate others or misrepresent affiliations;</li>
              <li>Do not infringe any intellectual property or privacy rights;</li>
              <li>Do not post viruses or harmful code;</li>
              <li>Do not use the Websites for solicitation or advertising.</li>
            </ul>
            <p className="text-gray-600 mb-4">
              Any feedback, suggestions, or ideas submitted to /tmp labs through the Websites shall be deemed
              non-confidential. /tmp labs is free to use such feedback without restriction or obligation to compensate you.
            </p>

                <div id="social" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">10</span>
                    Social Media
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              These Terms also apply when engaging with /tmp labs's official social media channels, including those
              operated on platforms such as Facebook, LinkedIn, Instagram, YouTube, and X (formerly Twitter). By
              accessing or interacting with our social media pages, you agree to comply with these Terms and the
              terms of the relevant social media platform.
            </p>
            <p className="text-gray-600 mb-4">
              We use social media to share information about our services, news, updates, events, campaigns, and
              topics of interest. Engagement with third-party content, such as liking or reposting, does not
              constitute an endorsement.
            </p>
            <p className="text-gray-600 mb-4">
              User-generated content (e.g., comments, tags) remains the responsibility of the original poster and
              does not necessarily reflect the views of /tmp labs. We may remove or report content that:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Is offensive, defamatory, or irrelevant;</li>
              <li>Breaches privacy;</li>
              <li>Violates platform terms or applicable law.</li>
            </ul>
            <p className="text-gray-600 mb-4">
              You may not use our social media channels to advertise, harass, or misrepresent. By posting on our
              pages, you grant /tmp labs a non-exclusive, worldwide, royalty-free license to use, share, or republish
              your content. You represent that you have the rights necessary to grant this license.
            </p>

                <div id="children" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">11</span>
                    Children's Privacy
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              /tmp labs does not knowingly collect information from children under the age of 13. If you are under
              18 years of age, you must have parental or guardian consent to use this platform. For more details,
              please refer to our <a href="/privacy" className="text-edu-blue hover:underline">Privacy Policy</a>.
            </p>

                <div id="partner" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">12</span>
                    Partner Agencies
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              If you register through a partner education consultant or agency, your data may be shared with that
              partner to facilitate your study abroad application process. Partners are bound by separate agreements
              to protect your information and comply with applicable data protection laws.
            </p>

                <div id="modifications" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">13</span>
                    Modifications and Termination
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              /tmp labs may modify, suspend, or discontinue any part of the Websites or these Terms at any time.
              /tmp labs may also terminate your access at its sole discretion, with or without notice. /tmp labs
              reserves the right to investigate any suspected violation of these Terms and may disclose information
              as necessary to comply with legal obligations or protect its rights, users, or the public.
            </p>

                <div id="liability" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">14</span>
                    Limitation of Liability
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              To the fullest extent permitted by law, /tmp labs disclaims all liability for any damages arising out
              of or in connection with your use of the Websites. This includes, without limitation, indirect,
              incidental, or consequential damages, data loss, or loss of profits.
            </p>
            <p className="text-gray-600 mb-4">
              Nothing in these Terms excludes or limits any rights that cannot be excluded under applicable
              consumer protection laws.
            </p>

                <div id="indemnification" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">15</span>
                    Indemnification
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              You agree to indemnify and hold harmless /tmp labs and its officers, directors, employees, and agents
              from any claims, damages, liabilities, or expenses arising from your use of the Websites or violation
              of these Terms.
            </p>

                <div id="governing" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">16</span>
                    Governing Law and Jurisdiction
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              These Terms are governed by the laws of Singapore. You agree to submit to the exclusive jurisdiction of
              the Singapore courts in relation to any dispute arising under or in connection with these Terms.
            </p>

                <div id="contact" className="scroll-mt-4 pt-6 border-t border-gray-100">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">17</span>
                    Notice and Contact
                  </h2>
                </div>
            <p className="text-gray-600 mb-4">
              All legal notices, including notices of IP infringement, should be sent to:
            </p>
                <div className="bg-edu-blue/5 border border-edu-blue/20 p-6 rounded-xl text-gray-700">
                  <p className="font-semibold text-edu-blue mb-2">/TMP LABS PTE. LTD.</p>
                  <p>Email: legal@tmplabs.ai</p>
                  <p>10 Ubi Crescent #04-21</p>
                  <p>Ubi Techpark</p>
                  <p>Singapore 408564</p>
                </div>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-edu-blue hover:bg-edu-blue-dark text-white p-3 rounded-full shadow-lg transition-all duration-300 z-50"
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Document Privacy Notice Page (GDPR/DPDP — document upload feature)
const DocumentPrivacyNoticePage = () => {
  return (
    <div className="bg-slate-950 text-slate-200" style={{ minHeight: "100dvh" }}>
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-100 mb-1">Document Privacy Notice</h1>
          <p className="text-xs text-slate-500">Version 1.0 · March 2026 · /tmp Labs Pvt. Ltd.</p>
        </div>

        <div className="space-y-8 text-sm text-slate-300 leading-relaxed">

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">1. What data we collect</h2>
            <p>When you upload documents to your Detailed Profile, we collect the document files themselves (passport, transcripts, degree certificates, CVs, test score reports, photos) along with metadata: file name, type, size, upload timestamp, and document ID.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">2. Why we collect it</h2>
            <p>Documents are collected to (a) verify your eligibility for study-abroad programmes, (b) allow your assigned counselor to review your application package, and (c) optionally, with your separate consent, enable AI-assisted AutoFill of your profile fields.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">3. Who can access your documents</h2>
            <p>Only your assigned counselor at your partner institution and authorised /tmp Labs platform staff (for support and security purposes) can access your documents. Documents are never shared with universities or third parties without your explicit consent.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">4. Where documents are stored</h2>
            <p>Documents are stored in Google Cloud Storage, region <strong>asia-south1 (Mumbai)</strong>, with server-side AES-256 encryption at rest and TLS in transit. Access is restricted by tenant-scoped signed URLs valid for 15 minutes.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">5. Retention</h2>
            <p>Documents are retained for <strong>2 years</strong> from the date of upload, or until you submit an erasure request — whichever comes first. We do not apply automatic deletion based on inactivity periods.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">6. Your rights (GDPR / DPDP)</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>Access</strong> — Request a copy of the data we hold about you.</li>
              <li><strong>Correction</strong> — Request correction of inaccurate data.</li>
              <li><strong>Erasure (Right to be Forgotten)</strong> — Request permanent deletion of all your documents and associated profile data.</li>
              <li><strong>Withdrawal of consent</strong> — Withdraw storage or AI-processing consent at any time. Withdrawal does not affect prior lawful processing.</li>
              <li><strong>Portability</strong> — Request your data in a machine-readable format.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">7. How to request erasure</h2>
            <p>Email <a href="mailto:privacy@tmplabs.ai" className="text-blue-400 underline">privacy@tmplabs.ai</a> with subject "Data Erasure Request" and your registered email address. We will respond within <strong>72 hours</strong> and issue an erasure certificate upon completion. Alternatively, your platform account includes a self-service erasure option under Account Settings.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-100 mb-2">8. Contact</h2>
            <p>
              <strong>/tmp Labs Pvt. Ltd.</strong><br />
              Data Protection contact: <a href="mailto:privacy@tmplabs.ai" className="text-blue-400 underline">privacy@tmplabs.ai</a><br />
              For queries about your study-abroad application data, contact your assigned counselor through the platform.
            </p>
          </section>

        </div>

        <div className="mt-10 pt-6 border-t border-white/8 text-xs text-slate-600">
          Document Privacy Notice v1.0 · March 2026 · /tmp Labs Pvt. Ltd. · This notice applies specifically to document uploads in the Detailed Profile feature.
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const branding = useBranding(); // v2.37: Tenant-aware branding
  const [showBackToTop, setShowBackToTop] = useState(false);

  const sections = [
    { id: 'pp-intro', title: '1. What is this Privacy Policy for?' },
    { id: 'pp-collect', title: '2. What Personal Data do we collect?' },
    { id: 'pp-use', title: '3. What do we use Personal Data for?' },
    { id: 'pp-cookies', title: '4. How do we use cookies?' },
    { id: 'pp-share', title: '5. Who do we share Personal Data with?' },
    { id: 'pp-rights', title: '6. What are your rights?' },
    { id: 'pp-security', title: '7. How do we keep your data safe?' },
    { id: 'pp-retention', title: '8. How long do we keep data?' },
    { id: 'pp-children', title: '9. Children under 13' },
    { id: 'pp-transfer', title: '10. International transfers' },
    { id: 'pp-changes', title: '11. Will this Policy change?' },
    { id: 'pp-contact', title: '12. Contacting us' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-edu-blue/5" style={{ minHeight: "100dvh" }}>
      {/* Brand Header */}
      <div className="bg-gradient-to-r from-edu-blue to-edu-blue-dark text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="mr-4 text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Privacy Policy</h1>
                <p className="text-white/80 text-sm">How we collect, use, and protect your data</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-2 text-sm text-white/80">
              <span>Last Updated: January 2025</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Sidebar */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="lg:sticky lg:top-4">
              <Card className="shadow-lg border-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Table of Contents
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <nav className="space-y-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg transition-colors hover:bg-edu-blue/10 hover:text-edu-blue text-gray-600"
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Card className="shadow-lg border-0">
              <CardContent className="p-8 lg:p-10">
                {/* Mobile Last Updated */}
                <p className="text-sm text-gray-500 mb-6 md:hidden">Last Updated: January 2025</p>

                {/* Section 1 */}
                <div id="pp-intro" className="scroll-mt-4">
                  <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                    <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">1</span>
                    What is this Privacy Policy for?
                  </h2>
                </div>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">Introduction</h3>
            <p className="text-gray-600 mb-4">
              /TMP LABS PTE. LTD. and our related entities ("/tmp labs", "we", "us", or "our") are committed to protecting
              your privacy. This Privacy Policy outlines how we collect, use, share and process identifiable information
              about you ("Personal Data") across all websites, applications and platforms that we own or operate, including
              {branding?.platform_name || '∑n-route'} and related services ("Services"). Unless specified otherwise, /TMP LABS PTE. LTD. acts as the
              controller of your Personal Data. This Privacy Policy also explains your privacy rights.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">Third Party Links and Platforms</h3>
            <p className="text-gray-600 mb-4">
              Our Services may contain links to websites, applications, platforms and services maintained by third parties.
              The privacy practices of these third parties, including social media platforms that host our branded content,
              are governed by their respective privacy policies. We encourage you to review their policies to better
              understand their privacy practices.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">/tmp labs's Role as a Processor</h3>
            <p className="text-gray-600 mb-4">
              This Privacy Policy does not apply to the extent /tmp labs processes Personal Data in the role of a processor
              or service provider on behalf of our customers. For example, where our customer is an education consultancy
              or organisation that has purchased our Services for its own students or users, they control the data processing,
              and their privacy practices apply. /tmp labs is not responsible for the data privacy and security practices
              of its customers.
            </p>

            {/* Section 2 */}
            <div id="pp-collect" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">2</span>
                What Personal Data do we collect?
              </h2>
            </div>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">Directly or Automatically</h3>
            <p className="text-gray-600 mb-4">
              We may collect Personal Data both directly from you, and automatically when you interact with our Services.
              The Personal Data we collect from you may include identifiers, financial information, commercial details,
              educational records, visual data, professional or employment-related information, internet activity, as well
              as any other such information from time-to-time. Below are the situations in which we collect this information:
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <table className="w-full text-sm text-gray-600">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-semibold">Situation</th>
                    <th className="text-left py-2 font-semibold">Types of Personal Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="py-3 pr-4">Creating an account or registering for our Services</td>
                    <td className="py-3">Name, email address, phone number, password, educational information</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Building your student profile for university applications</td>
                    <td className="py-3">Academic transcripts, test scores (IELTS, TOEFL, GRE, GMAT), certificates, passport details, nationality, date of birth</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Uploading documents</td>
                    <td className="py-3">Identity documents, academic records, financial documents, letters of recommendation</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Interacting with our websites or mobile app</td>
                    <td className="py-3">IP address, device information, browser type, usage data, cookies</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Using AI-powered recommendations</td>
                    <td className="py-3">Your preferences, search history, saved programs, application interests</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">Communicating with us or partner consultants</td>
                    <td className="py-3">Contact information, message content, communication preferences</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">Indirectly</h3>
            <p className="text-gray-600 mb-4">
              We may also collect Personal Data indirectly from third party sources such as partner education consultancies,
              universities, or from publicly available sources. We may generate information based on the Personal Data
              provided by you combined with Personal Data from such third party or public sources in accordance with
              applicable law.
            </p>
            <p className="text-gray-600 mb-4">
              If you provide us or our affiliates with any Personal Data relating to other individuals, you represent that
              you have the authority to do so and have obtained the necessary consent (where required).
            </p>

            {/* Section 3 */}
            <div id="pp-use" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">3</span>
                What do we use Personal Data for?
              </h2>
            </div>
            <p className="text-gray-600 mb-4">/tmp labs processes the Personal Data collected about you for a number of purposes, including:</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">(1) To perform a contract or to take steps prior to entering a contract:</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Delivering, administering or providing our Services to you</li>
              <li>Processing university applications on your behalf</li>
              <li>Verifying your identity and academic credentials</li>
              <li>Sending you necessary communications about your applications</li>
              <li>Providing you with customer service or support</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">(2) Where you have given consent to the processing of your Personal Data:</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Sending you information about /tmp labs's products and Services</li>
              <li>Sharing your Personal Data with partner education consultancies</li>
              <li>Submitting your profile and documents to universities</li>
              <li>Placing cookies and other similar technologies</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">(3) Where necessary to conduct our business and pursue our legitimate interests:</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Providing AI-powered program recommendations</li>
              <li>Analysing and measuring the effectiveness of our Services</li>
              <li>Diagnosing and addressing any problems with our Services</li>
              <li>Investigating and responding to any comments or complaints</li>
              <li>Conducting market research to improve user experience</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">(4) Where necessary for legal reasons:</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Responding to requests from government or law enforcement authorities</li>
              <li>Complying with judicial proceedings, court orders or legal processes</li>
              <li>Detecting, preventing, or addressing fraudulent or illegal activity</li>
            </ul>

            {/* Section 4 */}
            <div id="pp-cookies" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">4</span>
                How do we use cookies?
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              /tmp labs uses cookies when you interact with our website or Services. Cookies are small text files stored
              by your web browser when you visit websites. In addition to cookies, we may also use other similar technologies
              such as HTML5 Local Storage, web beacons, and embedded scripts.
            </p>
            <p className="text-gray-600 mb-4">By allowing cookies, we can improve your experience by:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Tailoring our website and content based on your preferences</li>
              <li>Keeping track of your settings and preferences for future visits</li>
              <li>Better understanding your likely interests to provide relevant recommendations</li>
              <li>Helping us enhance the performance of our website for you</li>
            </ul>
            <p className="text-gray-600 mb-4">
              You can control how /tmp labs uses cookies by adjusting your browser settings. If you choose to disable
              cookies, our websites may not function properly, and your user experience may be different from what you expect.
            </p>
            <p className="text-gray-600 mb-4">
              <strong>Strictly necessary cookies:</strong> These cookies are essential for our website to function and cannot
              be disabled. They are usually only set in response to actions you take, such as setting your privacy preferences,
              logging in, or filling out forms.
            </p>

            {/* Section 5 */}
            <div id="pp-share" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">5</span>
                Who do we share Personal Data with?
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              /tmp labs may share your Personal Data with various parties, either with your consent or where consistent
              with a purpose and legal basis outlined in this Privacy Policy, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>/tmp labs affiliates:</strong> Entities, affiliates or subsidiaries within our corporate group</li>
              <li><strong>Universities and Institutions:</strong> Educational institutions to process your applications</li>
              <li><strong>Partner Agencies:</strong> Education consultancies that you registered through or work with</li>
              <li><strong>Service providers:</strong> External entities that help us provide Services, including IT systems, analytics, customer support</li>
              <li><strong>Professional advisers:</strong> Lawyers, bankers, auditors, and accountants who provide professional services</li>
              <li><strong>Third parties involved in corporate transactions:</strong> Prospective purchasers in case of merger or acquisition</li>
              <li><strong>Public authorities:</strong> Regulatory, governmental or judicial bodies, where required by law</li>
            </ul>
            <p className="text-gray-600 mb-4">
              /tmp labs may share aggregated, anonymised information, such as statistical data to show trends about the
              general use of our Services.
            </p>

            {/* Section 6 */}
            <div id="pp-rights" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">6</span>
                What are your rights?
              </h2>
            </div>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">Your rights</h3>
            <p className="text-gray-600 mb-4">Where provided by applicable law, you may have the following rights concerning your Personal Data:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of your Personal Data</li>
              <li><strong>Correction:</strong> Correct, delete or restrict processing of your Personal Data</li>
              <li><strong>Information:</strong> Know more about how we process your Personal Data</li>
              <li><strong>Objection:</strong> Object to the processing of your Personal Data in certain circumstances</li>
              <li><strong>Portability:</strong> Request your data in a structured, commonly used format</li>
              <li><strong>Withdrawal:</strong> Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className="text-gray-600 mb-4">
              These rights may be limited in certain circumstances. For example, we may deny your request if fulfilling
              it would reveal personal information about another individual, or where data must be kept to fulfil legal
              or reporting obligations.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-3">Exercising your rights</h3>
            <p className="text-gray-600 mb-4">
              To exercise your rights, please contact us using the information in Section 12 below. We will aim to respond
              to legitimate requests within 30 days. You may also be able to exercise your rights by editing or deleting
              your Personal Data by logging into your account and accessing "Profile" settings.
            </p>
            <p className="text-gray-600 mb-4">
              You can opt-out of receiving marketing communications at any time by clicking the unsubscribe link at the
              bottom of our emails, or by contacting us directly.
            </p>

            {/* Section 7 */}
            <div id="pp-security" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">7</span>
                How do we keep your data safe?
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              /tmp labs takes appropriate technical, organisational and physical measures to help safeguard Personal Data
              against accidental, unlawful, or unauthorised alteration, destruction, loss or disclosure. Some of the
              security controls we use include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Two-factor authentication and identity verification</li>
              <li>Access controls and role-based permissions</li>
              <li>Regular security assessments and audits</li>
              <li>Appropriate contractual confidentiality obligations for employees and contractors</li>
            </ul>
            <p className="text-gray-600 mb-4">
              While we follow generally accepted standards to protect Personal Data, no method of storage or transmission
              is 100% secure. You remain responsible for limiting access to your devices, protecting your password, and
              signing out of the platform after each session.
            </p>

            {/* Section 8 */}
            <div id="pp-retention" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">8</span>
                How long do we keep data?
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              /tmp labs will retain and process your Personal Data for as long as we have a legal basis to do so. We will
              delete certain Personal Data once we no longer have a business reason to retain it. However, certain types
              of Personal Data may need to be retained even after you discontinue the Services in order for us to comply
              with legal, business and reporting obligations.
            </p>
            <p className="text-gray-600 mb-4">
              Application-related data is typically retained for 7 years for record-keeping and compliance purposes.
              Upon expiry of the applicable retention period, your Personal Data will be deleted or anonymised.
            </p>

            {/* Section 9 */}
            <div id="pp-children" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">9</span>
                Children under 13
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              /tmp labs's Services are not intended for children under the age of 13. We do not knowingly collect Personal
              Data from children under the age of 13 without appropriate consent from a parent, guardian or caregiver.
            </p>
            <p className="text-gray-600 mb-4">
              If you believe we have mistakenly or unintentionally collected Personal Data of a minor without appropriate
              consent, please contact us using the information in Section 12 below.
            </p>

            {/* Section 10 */}
            <div id="pp-transfer" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">10</span>
                International transfers
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              /tmp labs is a global organisation. Your Personal Data may be collected, transferred to and stored by
              /tmp labs, affiliates or third parties in various jurisdictions, including Singapore, India, and other
              countries where universities you apply to are located.
            </p>
            <p className="text-gray-600 mb-4">
              Therefore, your Personal Data may be processed outside your country or jurisdiction, including in places
              that may not provide for the same level of data protection as your home jurisdiction. We ensure that the
              recipient of your Personal Data offers an adequate level of protection and security, consistent with this
              Privacy Policy and applicable law.
            </p>

            {/* Section 11 */}
            <div id="pp-changes" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">11</span>
                Will this Policy change?
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              This Privacy Policy will be updated from time to time to accommodate and reflect changes to industry practices,
              new technologies, legal requirements, and other factors. If we make any changes, the "last updated" date at
              the top of this page will be modified, and the revised version will be published.
            </p>
            <p className="text-gray-600 mb-4">
              We encourage you to periodically review this Privacy Policy so that you remain informed about the Personal
              Data we collect, how we use it, and under what circumstances it may be disclosed. Under certain circumstances,
              we will notify you of material changes before they come into effect.
            </p>

            {/* Section 12 */}
            <div id="pp-contact" className="scroll-mt-4 pt-6 mt-6 border-t border-gray-100">
              <h2 className="text-xl font-semibold text-edu-blue mb-4 flex items-center">
                <span className="bg-edu-blue text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">12</span>
                Contacting us
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              /tmp labs has a Data Protection Officer to oversee our compliance with data protection obligations. The
              Data Protection Officer also handles data subject access requests and liaises with data privacy regulators
              in each jurisdiction /tmp labs operates in.
            </p>
            <p className="text-gray-600 mb-4">
              To exercise your rights regarding your Personal Data, or if you have any questions regarding this Privacy
              Policy or our data privacy practices, please email us at <strong>privacy@tmplabs.ai</strong> or write to us at:
            </p>
            <div className="bg-gradient-to-br from-edu-blue/5 to-edu-blue/10 p-6 rounded-xl border border-edu-blue/20 mb-4">
              <div className="flex items-start gap-4">
                <div className="bg-edu-blue/10 p-3 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-edu-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">/TMP LABS PTE. LTD.</p>
                  <p className="text-gray-600">Data Protection Officer</p>
                  <p className="text-gray-600">10 Ubi Crescent #04-21</p>
                  <p className="text-gray-600">Ubi Techpark</p>
                  <p className="text-gray-600">Singapore 408564</p>
                  <a href="mailto:privacy@tmplabs.ai" className="mt-3 inline-flex items-center text-edu-blue hover:text-edu-blue-dark font-medium">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    privacy@tmplabs.ai
                  </a>
                </div>
              </div>
            </div>
            <p className="text-gray-600 mb-4">
              When you contact us, please ensure you provide all relevant information to assist with your request or query,
              including the jurisdiction you reside in.
            </p>
            <p className="text-gray-600 mb-4">
              /tmp labs is committed to working with you to fairly resolve any complaint or concern about privacy. However,
              if you believe that we have not been able to assist with your complaint or concern, you may wish to exercise
              your right to lodge a complaint with the appropriate data protection authority in your jurisdiction.
            </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-edu-blue hover:bg-edu-blue-dark text-white p-3 rounded-full shadow-lg transition-all duration-300 z-50"
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Student Dashboard
const StudentDashboard = () => {
  // Utility to safely extract status text from object or string
  const toStatusText = (s) =>
    (s && typeof s === 'object' && 'value' in s)
      ? String(s.value)
      : String(s ?? '');

  const navigate = useNavigate();
  const user = getUser();
  const isMobile = useMobile();
  const branding = useBranding();
  const [profile, setProfile] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [trendingPrograms, setTrendingPrograms] = useState([]);
  const [applications, setApplications] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);
  const [tenantLogoAlt, setTenantLogoAlt] = useState('');

  // Redirect mobile users to MobileDashboard
  useEffect(() => {
    if (isMobile && typeof window !== 'undefined') {
      navigate('/mobile-dashboard', { replace: true });
    }
  }, [isMobile, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Allow student and superadmin
    if (user.role !== 'student' && user.role !== 'superadmin') {
      navigate('/partner-dashboard');
      return;
    }
    loadData();
  }, []);

  useEffect(() => {
    const fetchTenantLogo = async () => {
      try {
        const res = await axios.get(`${API}/tenant/public-settings`, {
          headers: getAuthHeaders()
        });
        const settings = res.data?.settings || {};
        if (settings.logo_url) {
          setTenantLogoUrl(settings.logo_url);
          setTenantLogoAlt(settings.logo_alt || '');
        }
      } catch (_) {}
    };
    fetchTenantLogo();
  }, []);

  const loadData = async () => {
    try {
      // For superadmin, don't load profile, just load programs, apps, and cart
      if (user.role === 'superadmin') {
        const [programsRes, appsRes, cartRes] = await Promise.all([
          axios.get(`${API}/programs?limit=6`, { headers: getAuthHeaders() }),
          axios.get(`${API}/applications`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
          axios.get(`${API}/cart`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
        ]);
        
        setProfile(null); // Superadmin doesn't have a profile
        setPrograms(programsRes.data.programs || programsRes.data);
        setApplications(appsRes.data);
        setCartCount(cartRes.data.length);
      } else {
        // For students, load all data including profile
        const [profileRes, programsRes, trendingRes, appsRes, cartRes] = await Promise.all([
          axios.get(`${API}/student/profile`, { headers: getAuthHeaders() }),
          axios.get(`${API}/programs?limit=6`, { headers: getAuthHeaders() }),
          axios.get(`${API}/programs/trending?limit=6`, { headers: getAuthHeaders() }),
          axios.get(`${API}/applications`, { headers: getAuthHeaders() }),
          axios.get(`${API}/cart`, { headers: getAuthHeaders() })
        ]);

        setProfile(profileRes.data);
        setPrograms(programsRes.data.programs || programsRes.data);
        setTrendingPrograms(trendingRes.data?.programs || []);
        setApplications(appsRes.data);
        setCartCount(cartRes.data.length);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };


  const handleLogout = () => {
    clearAuth();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: "100dvh" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-slate-950 pb-20 md:pb-0" style={{ minHeight: "100dvh" }}>
      {/* Mobile Header - only shows <768px */}
      <MobileHeader />

      {/* Desktop Header - only shows >=768px */}
      <header className="hidden md:block bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-white/10 sticky top-0 z-50 shadow-sm dark:shadow-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Platform branding */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <PlatformLogo size={40} showName={false} />
              <ECEWordmark showTagline size="lg" />
            </button>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigateToDashboard(navigate)} className="text-sm text-gray-700 dark:text-gray-300 hover:text-edu-blue dark:hover:text-blue-400">
                🏠 Home
              </button>
{/* Universities hidden for demo
              <button onClick={() => navigate('/universities')} className="text-sm text-gray-700 dark:text-gray-300 hover:text-edu-blue dark:hover:text-blue-400">
                🎓 Universities
              </button>
*/}
              <button onClick={() => navigate('/programs')} className="text-sm text-gray-700 dark:text-gray-300 hover:text-edu-blue dark:hover:text-blue-400">
                📚 Programs
              </button>
              <button onClick={() => navigate('/applications')} className="text-sm text-gray-700 dark:text-gray-300 hover:text-edu-blue dark:hover:text-blue-400">
                📝 Applications
              </button>
              <button onClick={() => navigate('/ama/explore')} className="text-sm text-gray-700 dark:text-gray-300 hover:text-edu-blue dark:hover:text-blue-400 flex items-center gap-1.5">
                <EazySparkIcon size={18} />
                <span>{branding.platform_name}</span>
              </button>
              {user?.role === 'superadmin' && (
                <button onClick={() => navigate('/admin/tenants')} className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold">
                  🏢 Tenants
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Tenant Switcher */}
              <TenantSwitcher />
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="relative"
                onClick={() => navigate('/cart')}
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-edu-blue text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>
              <span className="text-sm text-gray-600 dark:text-gray-300">{user?.name}</span>
              <ThemeToggle />
              <Button variant="outline" size="sm" onClick={handleLogout}>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Superadmin Banner */}
        {user?.role === 'superadmin' && (
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">🔐 Superadmin Access</h2>
                <p className="text-white/90 mb-4">You have full platform access. Manage tenants, AI, and access all features.</p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => navigate('/admin/tenants')} className="bg-white text-purple-600 hover:bg-gray-100">
                    🏢 Manage Tenants
                  </Button>
                  <Button onClick={() => navigate('/admin/ai-config')} className="bg-white text-purple-600 hover:bg-gray-100">
                    ⚙️ AI Configuration
                  </Button>
                  <Button onClick={() => navigate('/ai/explore-telemetry')} className="bg-white text-purple-600 hover:bg-gray-100">
                    Explore Telemetry
                  </Button>
{/* Partner Dashboard hidden for demo
                  <Button onClick={() => navigate('/partner-dashboard')} variant="outline" className="border-white text-white hover:bg-white/10">
                    📊 Partner Dashboard
                  </Button>
*/}
                  <Button onClick={() => navigate('/analytics')} variant="outline" className="border-white text-white hover:bg-white/10">
                    📈 Analytics
                  </Button>
                  <Button onClick={() => navigate('/ama/explore')} variant="outline" className="border-white text-white hover:bg-white/10 flex items-center gap-2">
                    <EazySparkIcon size={18} /> {branding.platform_name}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Banner with Dynamic CTAs - Condensed on mobile */}
        <div className="bg-gradient-to-r from-edu-blue to-edu-blue-dark rounded-2xl p-4 md:p-8 text-white mb-4 md:mb-8">
          <h1 className="text-xl md:text-3xl font-bold mb-1 md:mb-2">Hey {user?.name?.split(' ')[0]}! 👋</h1>
          <p className="text-white/90 text-sm md:text-base mb-3 md:mb-4">
            {user?.role === 'superadmin'
              ? 'Full platform control unlocked.'
              : "Find your perfect program with AI-powered search"}
          </p>

          {/* AI Search Bar - Compact on mobile */}
          {user?.role !== 'superadmin' && (
            <div className="mb-3 md:mb-6">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const query = e.target.querySelector('input').value;
                  navigate(`/ama/explore${query ? `?q=${encodeURIComponent(query)}` : ''}`);
                }}
                className="flex gap-2 max-w-xl"
              >
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search programs, countries, scholarships..."
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 pl-10 md:pl-12 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 border border-transparent dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-white/50 dark:focus:ring-blue-500 text-sm md:text-base"
                  />
                  <div className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">
                    <EazySparkIcon size={20} />
                  </div>
                </div>
                <Button type="submit" className="bg-white text-edu-blue hover:bg-gray-100 font-semibold px-4 md:px-6 text-sm md:text-base">
                  Search
                </Button>
              </form>
              {/* Try suggestions as horizontal scrollable chips on mobile */}
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1 scrollbar-hide md:block">
                <span className="text-xs text-white/60 whitespace-nowrap md:hidden">Try:</span>
                <button
                  type="button"
                  onClick={() => navigate('/ama/explore?q=Masters in Computer Science under €10,000')}
                  className="text-xs bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0"
                >
                  CS Masters &lt;€10k
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/ama/explore?q=Best programs for Indian students in Germany')}
                  className="text-xs bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0"
                >
                  Germany for Indians
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/ama/explore?q=affordable MBA in Ireland')}
                  className="text-xs bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 md:hidden"
                >
                  Affordable MBA
                </button>
                <p className="hidden md:block text-xs text-white/60 mt-2">Try: "Masters in Computer Science under €10,000" • "Best programs for Indian students in Germany"</p>
              </div>
            </div>
          )}

          {/* Dynamic Program Match Counter */}
          {user?.role !== 'superadmin' && profile?.profile_completion >= 50 && (
            <div className="bg-white/10 rounded-lg px-4 py-2 inline-block mb-4">
              <span className="text-sm font-medium">Based on your profile: </span>
              <span className="text-lg font-bold">50+ programs waiting for you</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            {user?.role !== 'superadmin' && (
              <>
                {/* Complete/Edit Profile Button */}
                {(profile?.profile_completion || 0) < 100 && (
                  <Button
                    onClick={() => navigate(profile?.quick_profile_completed ? '/profile?edit=true' : '/profile')}
                    className="bg-white text-edu-blue hover:bg-gray-100 font-semibold"
                  >
                    {profile?.quick_profile_completed ? 'Edit Profile' : 'Complete Profile'}
                  </Button>
                )}

                {/* View Top Matches - Only show when profile has enough data (>= 30%) */}
                {(profile?.profile_completion || 0) >= 30 ? (
                  <Button
                    onClick={() => navigate('/recommendations')}
                    variant="outline"
                    className="border-white text-white hover:bg-white/10 flex items-center gap-2"
                  >
                    <span>✨</span> View Your Top Matches
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="border-white/50 text-white/60 cursor-not-allowed flex items-center gap-2"
                    disabled
                    title="Complete at least 30% of your profile to unlock recommendations"
                  >
                    <span>🔒</span> Unlock Matches (Complete Profile)
                  </Button>
                )}
              </>
            )}

            {/* AI Search Button - Always visible, leads to AI Explore */}
            <Button onClick={() => navigate('/ama/explore')} variant="outline" className="border-white text-white hover:bg-white/10 flex items-center gap-2">
              <EazySparkIcon size={20} /> {branding.platform_name}
            </Button>
          </div>
        </div>

        {/* Quick Stats - Compact 3-column ribbon on mobile */}
        <div className="grid grid-cols-3 gap-2 md:gap-6 mb-4 md:mb-8">
          {user?.role !== 'superadmin' && (
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-3 md:pt-6 md:px-6">
                <div className="flex flex-col md:flex-row items-center md:justify-between text-center md:text-left">
                  <div className="order-2 md:order-1">
                    <p className="text-[10px] md:text-sm text-gray-600 dark:text-gray-300 truncate">Profile</p>
                    <p className="text-lg md:text-3xl font-bold text-edu-blue dark:text-blue-400">{profile?.profile_completion || 0}%</p>
                  </div>
                  <div className="order-1 md:order-2 w-8 h-8 md:w-12 md:h-12 bg-edu-blue/10 dark:bg-blue-900/50 rounded-lg flex items-center justify-center mb-1 md:mb-0">
                    <span className="text-base md:text-2xl">👤</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-3 md:pt-6 md:px-6">
              <div className="flex flex-col md:flex-row items-center md:justify-between text-center md:text-left">
                <div className="order-2 md:order-1">
                  <p className="text-[10px] md:text-sm text-gray-600 dark:text-gray-300 truncate">Apps</p>
                  <p className="text-lg md:text-3xl font-bold text-edu-green">{applications.length}</p>
                </div>
                <div className="order-1 md:order-2 w-8 h-8 md:w-12 md:h-12 bg-edu-green/10 dark:bg-green-900/50 rounded-lg flex items-center justify-center mb-1 md:mb-0">
                  <span className="text-base md:text-2xl">📝</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-3 md:pt-6 md:px-6">
              <div className="flex flex-col md:flex-row items-center md:justify-between text-center md:text-left">
                <div className="order-2 md:order-1">
                  <p className="text-[10px] md:text-sm text-gray-600 dark:text-gray-300 truncate">Viewed</p>
                  <p className="text-lg md:text-3xl font-bold text-edu-blue-light">0</p>
                </div>
                <div className="order-1 md:order-2 w-8 h-8 md:w-12 md:h-12 bg-edu-blue-light/10 dark:bg-blue-900/50 rounded-lg flex items-center justify-center mb-1 md:mb-0">
                  <span className="text-base md:text-2xl">🎓</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions - Compact 3-column on mobile */}
        <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-8">
          {/* EazyCourseEngine */}
          <button
            onClick={() => navigate('/ama/explore')}
            className="p-2.5 md:p-4 bg-[var(--brand-primary)] text-white rounded-lg shadow hover:shadow-lg hover:scale-[1.02] transition-all text-center"
          >
            <div className="flex items-center justify-center mb-1 md:mb-2">
              <EazySparkIcon size={28} />
            </div>
            <div className="text-[11px] md:text-sm font-bold">ECE</div>
            <div className="text-[9px] md:text-xs opacity-80 hidden md:block mt-1">AI-powered</div>
          </button>
          <button
            onClick={() => navigate('/programs')}
            className="p-2.5 md:p-4 bg-white dark:bg-slate-800 rounded-lg shadow hover:shadow-md dark:shadow-none dark:border dark:border-slate-700 dark:hover:bg-slate-700 transition text-center"
          >
            <div className="text-xl md:text-3xl mb-1 md:mb-2">📚</div>
            <div className="text-[11px] md:text-sm font-medium text-gray-900 dark:text-gray-100">Browse</div>
            <div className="text-[9px] md:text-xs text-gray-500 dark:text-gray-400 hidden md:block mt-1">Full catalog</div>
          </button>
          <button
            onClick={() => navigate('/saved-programs')}
            className="p-2.5 md:p-4 bg-white dark:bg-slate-800 rounded-lg shadow hover:shadow-md dark:shadow-none dark:border dark:border-slate-700 dark:hover:bg-slate-700 transition text-center"
          >
            <div className="text-xl md:text-3xl mb-1 md:mb-2">❤️</div>
            <div className="text-[11px] md:text-sm font-medium text-gray-900 dark:text-gray-100">Saved</div>
            <div className="text-[9px] md:text-xs text-gray-500 dark:text-gray-400 hidden md:block mt-1">Your shortlist</div>
          </button>
        </div>

        {/* Trending Programs Section - Desktop only (mobile uses MobileDashboard) */}
        {trendingPrograms.length > 0 && (
          <div className="mb-4 md:mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-50">Trending programs</h2>
              <button
                onClick={() => navigate('/programs?sort=trending')}
                className="text-sm text-sky-500 hover:text-sky-400"
              >
                See all &rarr;
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trendingPrograms.slice(0, 6).map((program) => {
                const matchScore = program.match_score;
                const hasRealScore = matchScore !== null && matchScore !== undefined && typeof matchScore === 'number';
                const matchColor = hasRealScore
                  ? matchScore >= 85 ? 'text-emerald-400' : matchScore >= 70 ? 'text-blue-400' : 'text-orange-400'
                  : '';
                const matchBg = hasRealScore
                  ? matchScore >= 85 ? 'bg-emerald-500/10' : matchScore >= 70 ? 'bg-blue-500/10' : 'bg-orange-500/10'
                  : '';

                return (
                  <Card
                    key={program.id}
                    className="card-hover cursor-pointer dark:bg-slate-800 dark:border-slate-700 relative overflow-hidden"
                    onClick={() => navigate(`/programs/${program.id}`)}
                  >
                    {/* Match Score Badge */}
                    {hasRealScore && (
                      <div className={`absolute top-3 right-3 ${matchBg} backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1`}>
                        <span className={`text-sm font-bold ${matchColor}`}>{matchScore}%</span>
                        <span className="text-xs text-slate-400">match</span>
                      </div>
                    )}
                    {/* Country Flag */}
                    {program.country && (
                      <div className="absolute top-3 left-3 bg-white/90 dark:bg-slate-700/90 backdrop-blur-sm rounded px-2 py-0.5">
                        <span className="text-xs">{program.country}</span>
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <img
                          src={getUniversityLogoUrl(program.university?.logo_url, program.university?.name, 48)}
                          alt=""
                          className="w-12 h-12 rounded-lg object-contain bg-gray-100 dark:bg-slate-700 p-1"
                          onError={(e) => { e.target.src = getUniversityFallbackUrl(program.university?.name, 48); }}
                        />
                        {program.scholarship_available && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-0">💰 Scholarship</Badge>
                        )}
                      </div>
                      <CardTitle className="text-base dark:text-white line-clamp-2">{program.name}</CardTitle>
                      <CardDescription className="dark:text-gray-300 line-clamp-1">{program.university?.name || program.university_name}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">💰</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formatTuition(program.tuition_annual || program.tuition_fee, { currency: program.tuition_currency, country: program.country })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">⏱️</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{program.duration_months ? `${program.duration_months} months` : program.duration || 'TBD'}</span>
                        </div>
                      </div>
                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {program.degree_level && (
                          <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-300">{program.degree_level}</span>
                        )}
                        {program.field_of_study && (
                          <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-300">{program.field_of_study}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {/* Loading skeleton placeholder */}
            {trendingPrograms.length === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse bg-slate-100 dark:bg-slate-800 rounded-lg h-64"></div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation Tabs */}
        <Tabs defaultValue="programs" className="space-y-4 md:space-y-6">
          <TabsList className="grid w-full grid-cols-2 h-10 md:h-11">
            <TabsTrigger value="programs" className="text-xs md:text-sm">Your Matches</TabsTrigger>
            <TabsTrigger value="applications" className="text-xs md:text-sm">My Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="programs">
            {/* Profile completion gate for match scores */}
            {(profile?.profile_completion || 0) < 50 && (
              <div className="mb-3 md:mb-4 p-2.5 md:p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-center">
                <span className="text-amber-700 dark:text-amber-300 text-xs md:text-sm">
                  🎯 <button onClick={() => navigate('/profile')} className="underline hover:no-underline font-medium">Complete profile</button> to see match scores
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {programs.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-lg font-bold dark:text-white mb-2">Loading programs...</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">We're finding the best matches for you.</p>
                </div>
              ) : (
                programs.map((program, idx) => {
                  // v1.4.68: Only show match scores when profile >= 50% complete
                  const profileComplete = (profile?.profile_completion || 0) >= 50;
                  const matchScore = program.match_score;
                  const hasRealScore = profileComplete && matchScore !== null && matchScore !== undefined && typeof matchScore === 'number';
                  const matchColor = hasRealScore
                    ? matchScore >= 85 ? 'text-emerald-400' : matchScore >= 70 ? 'text-blue-400' : 'text-orange-400'
                    : '';
                  const matchBg = hasRealScore
                    ? matchScore >= 85 ? 'bg-emerald-500/10' : matchScore >= 70 ? 'bg-blue-500/10' : 'bg-orange-500/10'
                    : '';

                  // TrustBadge: data quality indicator
                  const trustStatus = program.verification_status || program.trust_badge || 'provisional';
                  const trustBadgeConfig = {
                    verified: { label: '✓ Verified', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                    provisional: { label: '◐ Provisional', color: 'text-amber-500', bg: 'bg-amber-500/10' },
                    quarantined: { label: '⚠ Unverified', color: 'text-red-500', bg: 'bg-red-500/10' },
                  };
                  const trustBadge = trustBadgeConfig[trustStatus] || trustBadgeConfig.provisional;

                  return (
                  <Card key={program.id} className="card-hover cursor-pointer dark:bg-slate-800 dark:border-slate-700 relative overflow-hidden" onClick={() => navigate(`/programs/${program.id}`)}>
                    {/* Match Score Badge - only show if real score exists AND profile >= 50% */}
                    {hasRealScore ? (
                      <div className={`absolute top-3 right-3 ${matchBg} backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1`} title="Program Match Score">
                        <span className={`text-sm font-bold ${matchColor}`}>{matchScore}%</span>
                        <span className="text-xs text-slate-400">match</span>
                      </div>
                    ) : null}
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        {/* v1.4.20: Logo with gray background for visibility in all themes */}
                        <img
                          src={getUniversityLogoUrl(program.university?.logo_url, program.university?.name, 48)}
                          alt=""
                          className="w-12 h-12 rounded-lg object-contain bg-gray-100 dark:bg-slate-700 p-1"
                          onError={(e) => { e.target.src = getUniversityFallbackUrl(program.university?.name, 48); }}
                        />
                        <div className="flex items-center gap-1">
                          {program.scholarship_available && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-0">💰</Badge>
                          )}
                          {/* TrustBadge */}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${trustBadge.bg} ${trustBadge.color}`} title="Data quality indicator">
                            {trustBadge.label}
                          </span>
                        </div>
                      </div>
                      <CardTitle className="text-lg dark:text-white line-clamp-1">{program.name}</CardTitle>
                      <CardDescription className="dark:text-gray-300">{program.university?.name}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">📍 Location</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{program.university?.country || 'TBD'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">💰 Tuition</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formatTuition(program.tuition_annual, { currency: program.tuition_currency, country: program.university?.country || program.country })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">⏱️ Duration</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{program.duration_months} months</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
                })
              )}
            </div>
            {programs.length > 0 && (
              <div className="text-center mt-6">
                <Button onClick={() => navigate('/programs')} size="lg">View All Programs</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="applications">
            {applications.length === 0 ? (
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="text-center py-12">
                  <div className="text-6xl mb-4">🚀</div>
                  <h3 className="text-lg font-bold dark:text-white mb-2">Ready to start applying?</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    Find programs that match your goals and apply with just a few clicks.
                  </p>
                  <Button onClick={() => navigate('/programs')} className="bg-gradient-to-r from-blue-600 to-blue-500">
                    Browse Programs
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <Card key={app.id} className="card-hover cursor-pointer dark:bg-slate-800 dark:border-slate-700" onClick={() => navigate(`/applications/${app.id}`)}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <img src={app.university?.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">{app.program?.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{app.university?.name}</p>
                          </div>
                        </div>
                        {(() => {
                          const statusText = toStatusText(app.status);
                          const statusCfg = {
                            draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
                            submitted: { label: 'Submitted', className: 'bg-blue-100 text-blue-700' },
                            under_review: { label: 'Under Review', className: 'bg-yellow-100 text-yellow-700' },
                            accepted: { label: 'Accepted', className: 'bg-green-100 text-green-700' },
                            rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
                          }[statusText] ?? { label: statusText || 'Draft', className: 'bg-gray-100 text-gray-700' };
                          
                          return <Badge className={statusCfg.className}>{statusCfg.label}</Badge>;
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <div className="text-center mt-6">
                  <Button onClick={() => navigate('/applications')} size="lg">View All Applications</Button>
                </div>
              </div>
            )}
          </TabsContent>

{/* Universities tab content hidden for demo
          <TabsContent value="universities">
            <div className="text-center py-12">
              <Button onClick={() => navigate('/universities')}>View All Universities</Button>
            </div>
          </TabsContent>
*/}
        </Tabs>
      </div>

      {/* Floating Course Finder Button - above WhatsApp (bottom-20) and bottom nav */}
      <button
        onClick={() => navigate('/ama/explore')}
        className="fixed bottom-36 right-4 md:bottom-8 md:right-8 z-40 flex items-center justify-center w-14 h-14 bg-[var(--brand-primary)] text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
        title={`${branding?.platform_name || '∑n-route'} — AI Course Finder`}
      >
        <EazySparkIcon size={28} />
      </button>
      <PoweredByFooter />
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const user = getUser();
  if (!user) return <Navigate to="/login" />;

  // WhatsApp button moved to DashboardRoute (gated by experience_type)
  return <>{children}</>;
};

// Partner Dashboard
const PartnerDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);
  const [tenantLogoAlt, setTenantLogoAlt] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Allow partner, tenant_admin and superadmin
    if (user.role !== 'partner' && user.role !== 'tenant_admin' && user.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, []);

  useEffect(() => {
    const fetchTenantLogo = async () => {
      try {
        const res = await axios.get(`${API}/tenant/public-settings`, {
          headers: getAuthHeaders()
        });
        const settings = res.data?.settings || {};
        if (settings.logo_url) {
          setTenantLogoUrl(settings.logo_url);
          setTenantLogoAlt(settings.logo_alt || '');
        }
      } catch (_) {}
    };
    fetchTenantLogo();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/partner/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API}/partner/students`, { headers: getAuthHeaders() })
      ]);
      
      setStats(statsRes.data.statistics);
      setStudents(studentsRes.data.students || studentsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: "100dvh" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50" style={{ minHeight: "100dvh" }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button onClick={() => navigate('/partner-dashboard')} className="flex items-center hover:opacity-80 transition gap-2">
                {tenantLogoUrl ? (
                  <img src={tenantLogoUrl} alt={tenantLogoAlt || 'Home'} style={{ height: '56px', width: 'auto' }} />
                ) : (
                  <>
                    <GlobeMark id="partner-app-globe" size={32} />
                    <span className="text-sm font-semibold hidden sm:inline">
                      <span className="text-[var(--brand-primary)] dark:text-[#60A5FA]">Eazy</span>
                      <span className="text-[var(--brand-secondary)]">Course</span>
                      <span className="text-[var(--brand-primary)] dark:text-[#60A5FA]">Engine</span>
                    </span>
                  </>
                )}
                {!tenantLogoUrl && (
                  <span className="ml-2 text-sm text-gray-500 border-l border-gray-300 pl-3">Partner Portal</span>
                )}
              </button>
            </div>
            
            {/* Partner Navigation Links */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/partner-dashboard')} className="text-sm text-gray-700 hover:text-edu-blue">
                🏠 Home
              </button>
              <button onClick={() => navigate('/analytics')} className="text-sm text-gray-700 hover:text-edu-blue">
                📊 Analytics
              </button>
              <button onClick={() => navigate('/ai/services')} className="text-sm text-gray-700 hover:text-edu-blue">
                🤖 AI Services
              </button>
              <button onClick={() => navigate('/programs')} className="text-sm text-gray-700 hover:text-edu-blue">
                🎓 Programs
              </button>
              {(user?.role === 'tenant_admin' || user?.role === 'superadmin') && (
                <>
                  <button onClick={() => navigate('/tenant/admin')} className="text-sm text-gray-700 hover:text-edu-blue">
                    🏢 Tenant Admin
                  </button>
                  <button onClick={() => navigate('/tenant/audit-logs')} className="text-sm text-gray-700 hover:text-edu-blue">
                    📋 Audit Logs
                  </button>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-edu-blue/10 text-edu-blue border-edu-blue/20">Partner</Badge>
              <span className="text-sm text-gray-600">{user?.name}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-edu-blue to-edu-blue-dark rounded-2xl p-8 text-white mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Partner Dashboard 🤝</h1>
              <p className="text-white/90">Manage students and track applications across your organization</p>
            </div>
            <Button 
              onClick={() => navigate('/analytics')}
              className="bg-white text-edu-blue hover:bg-gray-100"
            >
              View Analytics
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Students</p>
                  <p className="text-3xl font-bold text-edu-blue">{stats?.total_students || 0}</p>
                </div>
                <div className="w-12 h-12 bg-edu-blue/10 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">👥</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Applications</p>
                  <p className="text-3xl font-bold text-edu-blue-light">{stats?.total_applications || 0}</p>
                </div>
                <div className="w-12 h-12 bg-edu-blue-light/10 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📋</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Draft Applications</p>
                  <p className="text-3xl font-bold text-gray-600">{stats?.draft_applications || 0}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">✏️</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Submitted</p>
                  <p className="text-3xl font-bold text-edu-green">{stats?.submitted_applications || 0}</p>
                </div>
                <div className="w-12 h-12 bg-edu-green/10 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">✅</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Students List */}
        <Card>
          <CardHeader>
            <CardTitle>Registered Students</CardTitle>
            <CardDescription>
              {students.length === 0 ? 'No students registered yet' : `Managing ${students.length} student(s)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {students.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No students have registered yet.</p>
                <p className="text-sm text-gray-500">Students will appear here once they create accounts.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {students.map((student) => (
                  <div key={student.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-edu-blue to-edu-blue-dark rounded-full flex items-center justify-center text-white font-bold">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold">{student.name}</h3>
                        <p className="text-sm text-gray-600">{student.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Profile Completion</p>
                        <p className="text-lg font-semibold text-edu-blue">
                          {student.profile?.profile_completion || 0}%
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Applications</p>
                        <p className="text-lg font-semibold text-edu-green">
                          {student.applications_count || 0}
                        </p>
                      </div>
                      <Badge variant="outline">Student</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Import new pages
// Lazy-loaded pages for code splitting (reduces initial bundle from 1.3MB)
const ProfileBuilder = lazy(() => import('./pages/ProfileBuilder'));
const DetailedProfile = lazy(() => import('./pages/DetailedProfile'));
const QuickProfile = lazy(() => import('./pages/QuickProfile'));
const ProgramsPage = lazy(() => import('./pages/ProgramsPage'));
const ProgramDetail = lazy(() => import('./pages/ProgramDetail'));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage'));
const ApplicationDetail = lazy(() => import('./pages/ApplicationDetail'));
const DesignSystemShowcase = lazy(() => import('./pages/DesignSystemShowcase'));
const MobileDashboard = lazy(() => import('./pages/MobileDashboard'));
const ApplicationCart = lazy(() => import('./pages/ApplicationCart'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const UserFlowPage = lazy(() => import('./pages/UserFlowPage'));
const PartnerLanding = lazy(() => import('./pages/PartnerLanding'));
const DebugPage = lazy(() => import('./pages/DebugPage'));
const AIRecommendations = lazy(() => import('./pages/AIRecommendations'));
const AdminAIConfig = lazy(() => import('./pages/AdminAIConfig'));
const AIServicesHub = lazy(() => import('./pages/AIServicesHub'));
const AIDocumentAnalysis = lazy(() => import('./pages/AIDocumentAnalysis'));
const AIApplicationAssistant = lazy(() => import('./pages/AIApplicationAssistant'));
const AIEssayReview = lazy(() => import('./pages/AIEssayReview'));
const AIExplore = lazy(() => import('./pages/AIExplore'));
const AMAExplore = lazy(() => import('./pages/AMAExplore'));
const AMAExploreV2 = lazy(() => import('./pages/AMAExploreV2'));
const ExploreTelemetry = lazy(() => import('./pages/ExploreTelemetry'));
const SavedPrograms = lazy(() => import('./pages/SavedPrograms'));
const SwipePrograms = lazy(() => import('./pages/SwipePrograms'));
const PartnerPriorities = lazy(() => import('./pages/PartnerPriorities'));
const ProgramSequenceSettings = lazy(() => import('./pages/ProgramSequenceSettings'));
const ProgramRequests = lazy(() => import('./pages/ProgramRequests'));
const CatalogSettings = lazy(() => import('./pages/CatalogSettings'));
const UniversitiesPage = lazy(() => import('./pages/UniversitiesPage'));
const UniversityDetail = lazy(() => import('./pages/UniversityDetail'));
const SuperadminTenants = lazy(() => import('./pages/SuperadminTenants'));
const GovernanceControlPlane = lazy(() => import('./pages/GovernanceControlPlane'));
const TenantDetail = lazy(() => import('./pages/TenantDetail'));
const SuperadminDashboard = lazy(() => import('./pages/SuperadminDashboard'));
const SuperadminUsers = lazy(() => import('./pages/SuperadminUsers'));
const SuperadminAuditLogs = lazy(() => import('./pages/SuperadminAuditLogs'));
const TenantAuditLogs = lazy(() => import('./pages/TenantAuditLogs'));
const TenantSettings = lazy(() => import('./pages/TenantSettings'));
const TenantAdminDashboard = lazy(() => import('./pages/TenantAdminDashboard'));
const InvitationAccept = lazy(() => import('./pages/InvitationAccept'));
const DataIngestionDashboard = lazy(() => import('./pages/DataIngestionDashboard'));
const UniversitiesBrowser = lazy(() => import('./pages/UniversitiesBrowser'));
const DashboardLegacy = lazy(() => import('./pages/DashboardLegacy'));
const DashboardV3 = lazy(() => import('./pages/DashboardV3'));
const NRouteDashboard = lazy(() => import('./pages/NRouteDashboard'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));

// Partner Portal Pages
const PartnerDashboardNew = lazy(() => import('./pages/partner/PartnerDashboardNew'));
const PartnerStudents = lazy(() => import('./pages/partner/PartnerStudents'));
const PartnerApplications = lazy(() => import('./pages/partner/PartnerApplications'));
const PartnerTeam = lazy(() => import('./pages/partner/PartnerTeam'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center bg-gray-50" style={{ minHeight: "100dvh" }}>
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-edu-blue mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);
import InstallPWA from './components/mobile/InstallPWA';
import AppErrorBoundary from './components/debug/AppErrorBoundary';

// Dashboard version router - uses REACT_APP_DASHBOARD_VERSION env var
// Set to "legacy" to use pre-v2.5.0 dashboard, "v3" (default) for new dashboard
const DashboardRouter = () => {
  const [profile, setProfile] = useState(null);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const user = getUser();
  const branding = useBranding(); // v2.38: Tenant-aware dashboard routing

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load tenant config and profile in parallel
      const [configRes, profileRes] = await Promise.all([
        axios.get(`${API_URL}/tenant/public-settings`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/student/profile`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);

      setTenantConfig(configRes.data);
      setProfile(profileRes.data);

      // Check if phone capture modal should show (v2.34.0 - Item 2: Phone Gate)
      // v2.34.1 - Bug 2 fix: Check both phone_e164 (new) and phone (legacy)
      // v2.38.1: Only show for partner experience type (counsellor-based flow)
      const skipCount = parseInt(localStorage.getItem('phone_capture_skip_count') || '0');
      const hasPhone = profileRes.data?.phone_e164 || profileRes.data?.phone;
      const tempExperienceType = configRes.data?.experience_type ||
        (branding?.platform_name === 'EazyCourseEngine' ? 'partner' : 'direct');

      if (!hasPhone && skipCount < 3 && tempExperienceType === 'partner') {
        setShowPhoneModal(true);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneModalComplete = () => {
    setShowPhoneModal(false);
    // Reload profile to get updated phone data
    loadData();
  };

  const handleDismissBanner = () => {
    localStorage.setItem('phone_banner_dismissed', 'true');
    setProfile(prev => ({ ...prev, _bannerDismissed: true }));
  };

  if (loading) {
    return <div className="flex items-center justify-center" style={{ minHeight: "100dvh" }}><div className="spinner"></div></div>;
  }

  // v2.38: Experience-type based dashboard routing
  // Future-proofs for Plan B (multi-country white-label partners)
  const experienceType = tenantConfig?.experience_type ||
    (branding?.platform_name === 'EazyCourseEngine' ? 'partner' : 'direct');

  const DashboardComponent = experienceType === 'direct' ? NRouteDashboard : DashboardV3;

  // Show banner if phone is missing and user skipped 3+ times (Item 2 - Phone Banner)
  // v2.34.1 - Bug 2 fix: Check both phone_e164 (new) and phone (legacy)
  // v2.38.1: Only show for partner experience type (counsellor-based flow)
  const skipCount = parseInt(localStorage.getItem('phone_capture_skip_count') || '0');
  const hasPhone = profile?.phone_e164 || profile?.phone;
  const bannerDismissed = localStorage.getItem('phone_banner_dismissed') === 'true';
  const showBanner = !hasPhone && skipCount >= 3 && !bannerDismissed && experienceType === 'partner';

  // v2.38.4: WhatsApp button only for partner experience (not direct/∑n-route)
  const showWhatsApp = user?.role === 'student' && experienceType === 'partner';

  return (
    <>
      {showPhoneModal && (
        <PhoneCaptureModal
          user={user}
          tenantConfig={tenantConfig}
          onComplete={handlePhoneModalComplete}
        />
      )}

      {/* Phone Contact Banner - shows after 3 skips */}
      {showBanner && (
        <div className="fixed top-16 md:top-20 left-0 right-0 z-40 px-4 py-2 md:px-6">
          <div className="max-w-4xl mx-auto bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3 shadow-lg backdrop-blur-sm">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Your counsellor can't reach you yet — add your phone number
              </p>
            </div>
            <Link to="/profile" className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors">
              Add Phone
            </Link>
            <button
              onClick={handleDismissBanner}
              className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <DashboardComponent profile={profile} />

      {/* WhatsApp chat button - only for partner experience */}
      {showWhatsApp && <WhatsAppChatButton />}
    </>
  );
};

// Main App Component
function App() {
  return (
    <BrandingProvider>
    <ThemeProvider>
    <div className="App dark:bg-slate-900 transition-colors overflow-x-hidden w-full" style={{ minHeight: "100dvh" }}>
      <BrowserRouter>
        <AppErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<NrouteLanding />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-notice" element={<DocumentPrivacyNoticePage />} />
          <Route path="/invitation/accept/:token" element={<InvitationAccept />} />
          <Route path="/design-system" element={<DesignSystemShowcase />} />
          <Route path="/user-flow" element={<UserFlowPage />} />
          <Route path="/debug/roles" element={<ProtectedRoute><RoleAccessDemo /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
          <Route path="/dashboard-legacy" element={<ProtectedRoute><DashboardLegacy /></ProtectedRoute>} />
          <Route path="/superadmin-dashboard" element={<SuperAdminGuard><SuperadminDashboard /></SuperAdminGuard>} />
          <Route path="/governance" element={<GovernanceGuard><GovernanceControlPlane /></GovernanceGuard>} />
          <Route path="/mobile-dashboard" element={<ProtectedRoute><MobileDashboard /></ProtectedRoute>} />
          <Route path="/partner-dashboard" element={<PartnerGuard><PartnerDashboardNew /></PartnerGuard>} />
          <Route path="/partner/students" element={<PartnerGuard><PartnerStudents /></PartnerGuard>} />
          <Route path="/partner/applications" element={<PartnerGuard><PartnerApplications /></PartnerGuard>} />
          <Route path="/partner/team" element={<PartnerGuard><PartnerTeam /></PartnerGuard>} />
          <Route path="/analytics" element={<AdminGuard><AnalyticsDashboard /></AdminGuard>} />
          <Route path="/partner-priorities" element={<PartnerGuard><PartnerPriorities /></PartnerGuard>} />
          <Route path="/program-sequence" element={<PartnerGuard><ProgramSequenceSettings /></PartnerGuard>} />
          <Route path="/catalog-settings" element={<AdminGuard><CatalogSettings /></AdminGuard>} />
          <Route path="/program-requests" element={<AdminGuard><ProgramRequests /></AdminGuard>} />
          <Route path="/profile" element={<ProtectedRoute><QuickProfile /></ProtectedRoute>} />
          <Route path="/profile/quick" element={<ProtectedRoute><QuickProfile /></ProtectedRoute>} />
          <Route path="/profile/detailed" element={<ProtectedRoute><DetailedProfile /></ProtectedRoute>} />
          <Route path="/universities" element={<ProtectedRoute><UniversitiesPage /></ProtectedRoute>} />
          <Route path="/universities/:id" element={<ProtectedRoute><UniversityDetail /></ProtectedRoute>} />
          <Route path="/programs" element={<ProtectedRoute><ProgramsPage /></ProtectedRoute>} />
          <Route path="/programs/:id" element={<ProtectedRoute><ProgramDetail /></ProtectedRoute>} />
          <Route path="/cart" element={<ProtectedRoute><ApplicationCart /></ProtectedRoute>} />
          <Route path="/recommendations" element={<ProtectedRoute><AIRecommendations /></ProtectedRoute>} />
          <Route path="/ai/recommendations" element={<ProtectedRoute><AIRecommendations /></ProtectedRoute>} />
          <Route path="/ai/explore" element={<ProtectedRoute><AIExplore /></ProtectedRoute>} />
          <Route path="/ama/explore" element={<ProtectedRoute><AMAExplore /></ProtectedRoute>} />
          <Route path="/ama/explore-v2" element={<ProtectedRoute><AMAExploreV2 /></ProtectedRoute>} />
          <Route path="/ai/explore-telemetry" element={<SuperAdminGuard><ExploreTelemetry /></SuperAdminGuard>} />
          <Route path="/admin/ai-config" element={<AdminGuard><AdminAIConfig /></AdminGuard>} />
          <Route path="/admin/tenants" element={<SuperAdminGuard><SuperadminTenants /></SuperAdminGuard>} />
          <Route path="/admin/tenants/:tenantId" element={<SuperAdminGuard><TenantDetail /></SuperAdminGuard>} />
          <Route path="/admin/users" element={<SuperAdminGuard><SuperadminUsers /></SuperAdminGuard>} />
          <Route path="/admin/audit-logs" element={<SuperAdminGuard><SuperadminAuditLogs /></SuperAdminGuard>} />
          <Route path="/admin/data-ingestion" element={<AdminGuard><DataIngestionDashboard /></AdminGuard>} />
          <Route path="/admin/universities" element={<SuperAdminGuard><UniversitiesBrowser /></SuperAdminGuard>} />
          <Route path="/tenant/admin" element={<AdminGuard><TenantAdminDashboard /></AdminGuard>} />
          <Route path="/tenant/settings" element={<AdminGuard><TenantSettings /></AdminGuard>} />
          <Route path="/tenant/details" element={<AdminGuard><TenantDetail /></AdminGuard>} />
          <Route path="/settings/tenant" element={<Navigate to="/tenant/admin" replace />} />
          <Route path="/tenant/audit-logs" element={<AdminGuard><TenantAuditLogs /></AdminGuard>} />
          <Route path="/ai/services" element={<AdminGuard><AIServicesHub /></AdminGuard>} />
          <Route path="/ai/document-analysis" element={<ProtectedRoute><AIDocumentAnalysis /></ProtectedRoute>} />
          <Route path="/ai/application-assistant" element={<ProtectedRoute><AIApplicationAssistant /></ProtectedRoute>} />
          <Route path="/ai/essay-review" element={<ProtectedRoute><AIEssayReview /></ProtectedRoute>} />
          <Route path="/saved-programs" element={<ProtectedRoute><SavedPrograms /></ProtectedRoute>} />
          <Route path="/swipe-programs" element={<ProtectedRoute><SwipePrograms /></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute><ApplicationsPage /></ProtectedRoute>} />
          <Route path="/applications/:id" element={<ProtectedRoute><ApplicationDetail /></ProtectedRoute>} />
          <Route path="/eazylink" element={<PartnerLanding />} />
          <Route path="/debug" element={<DebugPage />} />
          </Routes>
          </Suspense>
        </AppErrorBoundary>
        <InstallPWA />
        <AuthenticatedBottomNav />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </ThemeProvider>
    </BrandingProvider>
  );
}

// Bottom navigation wrapper - only shows for authenticated users
function AuthenticatedBottomNav() {
  const location = useLocation();
  const user = getUser();

  // Pages where bottom nav should NOT appear
  const excludedPaths = ['/', '/login', '/register', '/waitlist', '/terms', '/privacy', '/invitation', '/eazylink', '/design-system', '/user-flow'];

  // Check if current path should exclude bottom nav
  const shouldHide = excludedPaths.some(path =>
    location.pathname === path || location.pathname.startsWith('/invitation/')
  );

  if (!user || shouldHide) return null;

  return <BottomNavigation userRole={user.role} />;
}

export default App;
